// ## Regular pages
//
// The breadcrumbs are a bit of a mess, since the site structure is not setup correctly.
// See these examples with url -> parentUrl.
// - /de-de/geiger-gruppe/familie-vision-werte/ -> /de-de/ (no direct parent; available in the menu through "Geiger Gruppe")
// - /de-de/geiger-gruppe/innovation/ -> ? (Is a subpage of /de-de/geiger-gruppe/, but /de-de/geiger-gruppe/ leads to 404)
// - /de-de/karriere/geiger-als-arbeitgeber/ -> /de-de/ (no direct parent; available in the menu through "Karriere")
// - /de-de/karriere/direkteinsteigende/ -> ? (no direct parent; available in the menu through "Karriere")
//
// It is setup correctly for pages in the "Leistungen" menu though. So there we have:
// - /de-de/leistungen/abbruch/ -> /de-de/ (no direct parent; available in the menu through "Leistungen -> Abruch")
// - /de-de/leistungen/abbruch/referenzen/ -> /de-de/leistungen/abbruch/ (correct parent)
//
// We therefore generate the breadcrumbs based on the url parts split by "/" (ignoring the locale part).:
// - If it as 2 or less parts, we only have the homepage and the current page -> Only breadcrumb to homepage
// - If it has 3 or more parts, we can use the second last part as parent (e.g. /de-de/karriere/geiger-als-arbeitgeber/ -> /de-de/karriere)
//
// ## Article pages
//
// All article pages are available through the /articles path, no matter if they are news or references.
// We therefore have to check the type of the article to determine the correct parent page.

import { ArticleCategory, TransformedArticlePage, TransformedPage } from '~/transforms/transformPages';
import { GeigerLocale } from '../i18n';
import { BreadcrumbList, ListItem, WithContext } from 'schema-dts';

// We need to manually define the parents here since there is no feasible way to determine them otherwise.
// If it is null, then there are no overview pages for the articles in that locale.
const articleParents: Record<GeigerLocale, Record<ArticleCategory, string | null>> = {
  [GeigerLocale.DE_DE]: {
    Neuigkeiten: '/de-de/neuigkeiten/',
    Referenz: '/de-de/referenzen/',
    Pressemitteilung: '/de-de/neuigkeiten/',
  },
  [GeigerLocale.AT_DE]: {
    Neuigkeiten: '/at-de/neuigkeiten-und-referenzen/',
    Referenz: '/at-de/neuigkeiten-und-referenzen/',
    Pressemitteilung: '/at-de/neuigkeiten-und-referenzen/',
  },
  [GeigerLocale.IT_IT]: {
    Neuigkeiten: null,
    Referenz: null,
    Pressemitteilung: null,
  },
  [GeigerLocale.RO_RO]: {
    Neuigkeiten: '/ro-ro/stiri-actuale/',
    Referenz: '/ro-ro/referinte/',
    Pressemitteilung: '/ro-ro/stiri-actuale/',
  },
};

const homeNames: Record<GeigerLocale, string> = {
  [GeigerLocale.DE_DE]: 'Startseite',
  [GeigerLocale.AT_DE]: 'Startseite',
  [GeigerLocale.IT_IT]: 'Pagina iniziale',
  [GeigerLocale.RO_RO]: 'Pagina principală',
};

/**
 * Generates breadcrumbs JSON-LD schema from page data
 * @param pageData The transformed page data
 * @returns breadcrumbs schema object
 */
export function generateBreadcrumbsSchema(
  pageData: TransformedPage,
  locale: GeigerLocale
): WithContext<BreadcrumbList> {
  const homeBreadcrumb: ListItem = {
    '@type': 'ListItem',
    position: 1,
    name: homeNames[locale],
    item: `${process.env.APP_URL}/${locale}`,
  };

  const itemListElement: ListItem[] = [homeBreadcrumb];

  if (pageData.contentType === 'article') {
    const articleData = pageData as TransformedArticlePage;
    const parentUrl = articleParents[locale][articleData.category as ArticleCategory];
    if (parentUrl) {
      itemListElement.push({
        '@type': 'ListItem',
        position: 2,
        name: '',
        item: `${process.env.APP_URL}${parentUrl}`,
      });
    }
  }

  if (pageData.contentType === 'legalPage') {
    itemListElement.push({
      '@type': 'ListItem',
      position: 2,
      name: pageData.meta.title || pageData.name,
      item: `${process.env.APP_URL}/${pageData.fullSlug}`,
    });
  }

  if (pageData.contentType === 'page') {
    const slugParts = pageData.fullSlug.split('/');
    if (slugParts.length > 3) {
      const parentSlug = slugParts.slice(0, -1).join('/');
      itemListElement.push({
        '@type': 'ListItem',
        position: 2,
        name: '',
        item: `${process.env.APP_URL}/${parentSlug}`,
      });
    }
  }

  itemListElement.push({
    '@type': 'ListItem',
    position: itemListElement.length + 1,
    name: pageData.meta.title || pageData.name,
    item: `${process.env.APP_URL}/${pageData.fullSlug}`,
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: itemListElement,
  };
}
